/**
 * The application shell: rail, top bar, banners, content.
 *
 * Rebuilt against the approved prototype. The rail is a floating rounded panel rather than a
 * flush sidebar, it collapses to its 44px icon circles, and it groups the eight registers
 * under a heading that remembers whether it was open. That is not decoration — a governance
 * tool is used all day by people who know exactly where they are going, and the collapsed
 * rail is what gives a 29-column register grid the width it needs.
 *
 * Every dimension here is a logical property — `inline-start`, never `left` — so Arabic flips
 * the whole layout by changing one attribute on `<html>` and nothing else. A component that
 * reaches for `left` is a component that will be wrong in Arabic, and finding those after the
 * fact costs an order of magnitude more than never writing them.
 *
 * The rail is navigation, so it is a `<nav>` with a label; every icon-only control carries an
 * accessible name. Hands-free operation arrives at M8 and will use the same names.
 */
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'
import type { RegisterInfo } from '@/api/types'
import { usePreferences } from '@/app/preferences'
import { navigate, type Route } from '@/app/routes'
import { useSession } from '@/app/session'
import { Icon, Spark, type IconName } from '@/components/Icon'
import { PeriodSwitcher } from '@/components/PeriodSwitcher'
import { roleLabel } from '@/lib/labels'
import { registerLabel } from '@/lib/registers'

type Section = {
  key: string
  icon: IconName
  route: Route | null
  badge?: 'inbox'
}

// The order is the design's, and it is a claim about how the day runs: look at the
// portfolio, work through the projects, clear what is waiting on you, then the periodic
// machinery. Reports and the analytical screens sit below a divider because they are
// consulted rather than worked in.
const PRIMARY: Section[] = [
  { key: 'dashboard', icon: 'chart', route: { name: 'dashboard' } },
  { key: 'projects', icon: 'projects', route: { name: 'projects' } },
  { key: 'inbox', icon: 'applications', route: { name: 'inbox' }, badge: 'inbox' },
  { key: 'periods', icon: 'periods', route: { name: 'periods' } },
  { key: 'ingestion', icon: 'ingestion', route: { name: 'ingestion' } },
]

/**
 * Below the divider: consulted rather than worked in.
 *
 * Reports is here disabled because it is designed, scheduled, and genuinely coming — a
 * greyed row with a "coming soon" title is an honest statement about a thing that exists on
 * paper.
 *
 * Intelligence and Lifecycle were here on the same terms and have been removed. They had no
 * screen, no endpoint, no artboard and no written definition — nobody could say what
 * "Lifecycle" does. A disabled row for something nobody can describe is not showing the
 * shape of the product; it is advertising two features to everyone the product is
 * demonstrated to. They come back the day someone writes down what they are.
 */
const SECONDARY: Section[] = [{ key: 'reports', icon: 'reports', route: null }]

// Configuration, and only an administrator may reach it. Hidden rather than disabled for
// everyone else: a disabled control tells a coordinator there is a screen they are missing,
// which is an invitation to ask for it. The API refuses them regardless — this is the rail
// being honest about what the person in front of it can do.
const ADMIN: Section[] = [{ key: 'builder', icon: 'registers', route: { name: 'builder', slug: 'risks' } }]

export function Shell({
  children,
  active = 'dashboard',
}: {
  children: ReactNode
  active?: string
}) {
  const { t, i18n } = useTranslation()
  const { theme, toggleTheme, density, toggleDensity, language, setLanguage } = usePreferences()
  const { railOpen, toggleRail, registersOpen, toggleRegisters } = usePreferences()
  const user = useSession((state) => state.user)
  const isAdmin = useSession((state) => state.hasRole('pmo_admin', 'platform_admin'))
  const signOut = useSession((state) => state.signOut)

  const unread = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: ({ signal }) => api.get<{ unread: number }>('/notifications/unread', signal),
    // The badge is a nudge, not a live feed. A minute of staleness costs nothing and saves a
    // request on every screen change.
    staleTime: 60_000,
  })

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    staleTime: Infinity,
  })

  const initials = (user?.full_name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <div className="shell" data-rail={railOpen ? 'open' : 'closed'}>
      <nav className="rail" aria-label={t('a11y.mainNavigation')}>
        <div className="rail__panel" data-scroll="1">
          <div className="rail__head">
            <span className="rail__mark" aria-hidden="true">
              <Icon name="chart" size={18} strokeWidth={2.6} />
            </span>
            <span className="rail__brand">
              <span className="rail__brandName">{t('app.name')}</span>
              <span className="rail__brandOrg">{user?.org_slug ?? ''}</span>
            </span>
            <button
              type="button"
              className="rail__menu"
              onClick={toggleRail}
              aria-label={t('a11y.toggleRail')}
              aria-expanded={railOpen}
              data-testid="toggle-rail"
            >
              {/* One chevron, rotated. `chevronEnd` points at the inline end, so it is
                  already correct in Arabic and the rotation stays correct with it. */}
              <Icon name="chevronEnd" size={16} strokeWidth={2.2} className="rail__menuIcon" />
            </button>
          </div>

          <button
            type="button"
            className="rail__create"
            onClick={() => navigate({ name: 'create' })}
            data-testid="rail-create"
          >
            <span className="rail__createIcon">
              <Icon name="plus" size={20} strokeWidth={2.3} />
            </span>
            <span className="rail__label">{t('nav.newEntry')}</span>
          </button>

          <ul className="rail__list">
            {PRIMARY.map((section) => (
              <RailItem
                key={section.key}
                section={section}
                active={active}
                badge={section.badge === 'inbox' ? (unread.data?.unread ?? 0) : 0}
              />
            ))}
          </ul>

          <div className="rail__rule" />

          <button
            type="button"
            className="rail__group"
            onClick={toggleRegisters}
            aria-expanded={registersOpen}
            data-testid="toggle-registers"
          >
            <span className="rail__glyph">
              <Icon name="registers" size={19} />
            </span>
            <span className="rail__label rail__label--group">
              {t('nav.registers')}
              <Icon name="chevronDown" size={14} strokeWidth={2} className="rail__caret" />
            </span>
          </button>

          {registersOpen ? (
            <ul className="rail__list rail__list--sub">
              {(registers.data ?? []).map((register) => (
                <li key={register.slug}>
                  <button
                    type="button"
                    className="rail__sub"
                    // Its own register, across the portfolio. Every one of these used to
                    // navigate to the projects list — all eight to the same place, so from
                    // that list they did nothing at all. The endpoint for the portfolio-wide
                    // view existed the whole time; only the package-scoped one had a screen.
                    onClick={() => navigate({ name: 'register', slug: register.slug })}
                    title={registerLabel(t, register.slug, register.label)}
                    data-testid={`rail-register-${register.slug}`}
                  >
                    {/* The slug, not the position: a register keeps its colour when another
                        one is added above it. See --register-* in packages/tokens. */}
                    <span className="rail__dot" data-register={register.slug} aria-hidden="true" />
                    <span className="rail__label">
                      {registerLabel(t, register.slug, register.label)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="rail__rule" />

          <ul className="rail__list">
            {SECONDARY.map((section) => (
              <RailItem key={section.key} section={section} active={active} badge={0} />
            ))}
            {isAdmin
              ? ADMIN.map((section) => (
                  <RailItem key={section.key} section={section} active={active} badge={0} />
                ))
              : null}
          </ul>

          <div className="rail__foot">
            <button
              type="button"
              className="rail__setting"
              onClick={toggleTheme}
              data-testid="toggle-theme"
            >
              <span className="rail__glyph rail__glyph--quiet">
                <Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} />
              </span>
              <span className="rail__label">
                {t(theme === 'light' ? 'a11y.darkMode' : 'a11y.lightMode')}
              </span>
            </button>
            <button
              type="button"
              className="rail__setting"
              onClick={toggleDensity}
              data-testid="toggle-density"
            >
              <span className="rail__glyph rail__glyph--quiet">
                <Icon name="density" size={18} />
              </span>
              <span className="rail__label">
                {t(density === 'compact' ? 'a11y.comfortable' : 'a11y.compact')}
              </span>
            </button>

            {user ? (
              <div className="rail__user">
                <span className="rail__avatar" aria-hidden="true">
                  {initials}
                </span>
                <span className="rail__label rail__label--user">
                  <span className="rail__userName">{user.full_name}</span>
                  <span className="rail__userRole">{roleLabel(t, user.roles[0])}</span>
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </nav>

      <div className="shell__body">
        <header className="topbar">
          <label className="topbar__search">
            <Icon name="search" size={16} className="topbar__searchIcon" />
            <input
              type="search"
              className="topbar__input"
              placeholder={t('search.placeholder')}
              aria-label={t('search.placeholder')}
              data-testid="global-search"
            />
          </label>

          <PeriodSwitcher />

          <div className="topbar__actions">
            <button
              type="button"
              className="pill"
              onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
              aria-label={t('a11y.toggleLanguage')}
              data-testid="toggle-language"
              lang={language === 'en' ? 'ar' : 'en'}
            >
              {language === 'en' ? 'عربي' : 'EN'}
            </button>

            <button
              type="button"
              className="pill pill--icon"
              onClick={() => navigate({ name: 'inbox' })}
              aria-label={t('a11y.notifications')}
              data-testid="notifications"
            >
              <Icon name="bell" size={17} />
              {(unread.data?.unread ?? 0) > 0 ? (
                <span className="pill__dot" aria-hidden="true" />
              ) : null}
            </button>

            <button
              type="button"
              className="pill pill--copilot"
              // M8. Rendered disabled rather than hidden: it shows the shape of the product
              // without pretending a click will do something.
              disabled
              title={t('nav.copilotSoon')}
              data-testid="copilot"
            >
              <Spark />
              <span>{t('nav.copilot')}</span>
            </button>

            <button
              type="button"
              className="pill"
              onClick={() => void signOut()}
              data-testid="sign-out"
            >
              {t('auth.signOut')}
            </button>
          </div>
        </header>

        <div className="shell__content" dir={i18n.dir()}>
          {children}
        </div>
      </div>
    </div>
  )
}

function RailItem({
  section,
  active,
  badge,
}: {
  section: Section
  active: string
  badge: number
}) {
  const { t } = useTranslation()
  const label = t(`nav.${section.key}`)
  const current = section.key === active

  return (
    <li>
      <button
        type="button"
        className="rail__item"
        aria-current={current ? 'page' : undefined}
        // Sections still to come are rendered disabled rather than hidden: it shows the
        // shape of the product without pretending a click will do something.
        disabled={section.route === null}
        onClick={() => section.route && navigate(section.route)}
        title={section.route === null ? `${label} — ${t('nav.soon')}` : label}
        data-testid={`rail-${section.key}`}
      >
        <span className="rail__glyph">
          <Icon name={section.icon} size={19} />
          {badge > 0 ? <span className="rail__badge">{badge > 99 ? '99+' : badge}</span> : null}
        </span>
        <span className="rail__label">{label}</span>
      </button>
    </li>
  )
}
